// MSSQL / T-SQL built-in function names emitted by `mssqlResolver` when the
// cursor is at a position that accepts a function call.
//
// MSSQL is case-insensitive for built-in functions but the manual renders the
// names in UPPER for readability, so we keep the curated list UPPER and
// lower-case at lookup time. Resolvers dedupe against user-defined routine
// names (case-insensitive) before emission so a stored function that shadows a
// built-in surfaces only once.
//
// Selection criteria: functions documented in the SQL Server Reference's
// "Built-in Functions" sections that surface in normal user authoring
// (aggregate / window / scalar / date-time / string / math / conversion /
// JSON helpers). Window-helper / extended-grouping operators that appear in
// the same suggest slot as functions (`OVER`, `PARTITION`) live in
// `mssqlKeywords` — they aren't function calls.
//
// NOTE: `GROUPING_SETS`, `ROLLUP`, and `CUBE` are technically GROUP BY
// extension keywords (not functions), but they surface in the same suggest
// slot as the grouping helper functions (`GROUPING`, `GROUPING_ID`). They are
// NOT in this list — `mssqlResolver` emits them as keyword-tier candidates
// only at `group-by` position via a dedicated helper.

const MSSQL_BUILTIN_FUNCTION_LIST: readonly string[] = [
  // Aggregate
  'APPROX_COUNT_DISTINCT', 'AVG', 'CHECKSUM_AGG', 'COUNT', 'COUNT_BIG',
  'GROUPING', 'GROUPING_ID', 'MAX', 'MIN', 'STDEV', 'STDEVP',
  'STRING_AGG', 'SUM', 'VAR', 'VARP',
  // Window / ranking
  'CUME_DIST', 'DENSE_RANK', 'FIRST_VALUE', 'LAG', 'LAST_VALUE', 'LEAD',
  'NTILE', 'PERCENT_RANK', 'PERCENTILE_CONT',
  'PERCENTILE_DISC', 'RANK', 'ROW_NUMBER',
  // String
  'ASCII', 'CHAR', 'CHARINDEX', 'CONCAT', 'CONCAT_WS', 'DIFFERENCE',
  'FORMAT', 'LEFT', 'LEN', 'LOWER', 'LTRIM', 'NCHAR', 'PATINDEX',
  'QUOTENAME', 'REPLACE', 'REPLICATE', 'REVERSE', 'RIGHT', 'RTRIM',
  'SOUNDEX', 'SPACE', 'STR', 'STRING_ESCAPE', 'STRING_SPLIT', 'STUFF',
  'SUBSTRING', 'TRANSLATE', 'TRIM', 'UNICODE', 'UPPER',
  // Date / time
  'CURRENT_TIMESTAMP', 'CURRENT_TIMEZONE',
  'CURRENT_TIMEZONE_ID', 'DATEADD', 'DATEDIFF', 'DATEDIFF_BIG',
  'DATEFROMPARTS', 'DATENAME', 'DATEPART', 'DATETIME2FROMPARTS',
  'DATETIMEFROMPARTS', 'DATETIMEOFFSETFROMPARTS', 'DATETRUNC', 'DAY',
  'EOMONTH', 'GETDATE', 'GETUTCDATE', 'ISDATE', 'MONTH',
  'SMALLDATETIMEFROMPARTS', 'SWITCHOFFSET', 'SYSDATETIME',
  'SYSDATETIMEOFFSET', 'SYSUTCDATETIME', 'TIMEFROMPARTS', 'TODATETIMEOFFSET',
  'YEAR',
  // Math
  'ABS', 'ACOS', 'ASIN', 'ATAN', 'ATN2', 'CEILING', 'COS', 'COT',
  'DEGREES', 'EXP', 'FLOOR', 'LOG', 'LOG10', 'PI', 'POWER', 'RADIANS',
  'RAND', 'ROUND', 'SIGN', 'SIN', 'SQRT', 'SQUARE', 'TAN',
  // Conditional / null
  'CHOOSE', 'COALESCE', 'IIF', 'ISNULL', 'NULLIF',
  // Type conversion / formatting
  'CAST', 'CONVERT', 'PARSE', 'TRY_CAST', 'TRY_CONVERT', 'TRY_PARSE',
  // JSON
  'ISJSON', 'JSON_ARRAY', 'JSON_MODIFY', 'JSON_OBJECT', 'JSON_PATH_EXISTS',
  'JSON_QUERY', 'JSON_VALUE', 'OPENJSON',
  // System / metadata
  'APP_NAME', 'CURRENT_USER', 'DB_ID', 'DB_NAME', 'HOST_ID', 'HOST_NAME',
  'NEWID', 'NEWSEQUENTIALID', 'OBJECT_ID', 'OBJECT_NAME', 'OBJECT_SCHEMA_NAME',
  'ORIGINAL_LOGIN', 'SCHEMA_ID', 'SCHEMA_NAME', 'SESSION_USER',
  'SUSER_NAME', 'SUSER_SNAME', 'SYSTEM_USER', 'USER_NAME',
  // Aggregate / analytic — null / hashing
  'CHECKSUM', 'BINARY_CHECKSUM', 'HASHBYTES',
];

export const MSSQL_BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(
  MSSQL_BUILTIN_FUNCTION_LIST.map((name) => name.toLowerCase()),
);

/** Lazy lookup; case-insensitive match against the curated set. */
export function isMssqlBuiltinFunction(name: string): boolean {
  return MSSQL_BUILTIN_FUNCTIONS.has(name.toLowerCase());
}

/**
 * The full curated list (UPPER per manual convention). Resolver consumers
 * dedupe against the user's schema routines (case-insensitive) before
 * emission.
 */
export function getMssqlBuiltinFunctions(): readonly string[] {
  return MSSQL_BUILTIN_FUNCTION_LIST;
}

/**
 * GROUP BY extension keywords that surface in the same suggest slot as
 * `GROUPING` / `GROUPING_ID` but are NOT function calls — they're syntactic
 * extensions to the GROUP BY clause. The resolver emits these only at the
 * `group-by` position (keyword tier) so they don't pollute select-list /
 * having / order-by suggestions.
 */
const MSSQL_GROUP_BY_EXTENSION_KEYWORDS: readonly string[] = [
  'GROUPING SETS', 'ROLLUP', 'CUBE',
];

export function getMssqlGroupByExtensionKeywords(): readonly string[] {
  return MSSQL_GROUP_BY_EXTENSION_KEYWORDS;
}
